import { api } from '@/lib/api';
import type {
  UserResponse,
  UserQuery,
  UserListResponse,
  CreateUserRequest,
  UpdateUserRequest,
} from '@troia-v3/types';

export const userService = {
  /**
   * Get all users with pagination and filters
   */
  async getUsers(query?: UserQuery): Promise<UserListResponse> {
    const response = await api.get('/users', { params: query });
    return response.data.data;
  },

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<UserResponse> {
    const response = await api.get(`/users/${id}`);
    return response.data.data;
  },

  /**
   * Create new user
   */
  async createUser(data: CreateUserRequest): Promise<UserResponse> {
    const response = await api.post('/users', data);
    return response.data.data;
  },

  /**
   * Update user
   */
  async updateUser(id: string, data: UpdateUserRequest): Promise<UserResponse> {
    const response = await api.put(`/users/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  /**
   * Activate user
   */
  async activateUser(id: string): Promise<UserResponse> {
    const response = await api.patch(`/users/${id}/activate`);
    return response.data.data;
  },

  /**
   * Deactivate user
   */
  async deactivateUser(id: string): Promise<UserResponse> {
    const response = await api.patch(`/users/${id}/deactivate`);
    return response.data.data;
  },

  /**
   * Reenviar convite (e-mail de verificação) para um usuário — ação de admin
   */
  async resendInvite(id: string): Promise<void> {
    await api.post(`/users/${id}/resend-invite`);
  },

  /**
   * Disparar redefinição de senha para um usuário — ação de admin
   */
  async resetPassword(id: string): Promise<void> {
    await api.post(`/users/${id}/reset-password`);
  },

  /**
   * Upload avatar for a specific user
   */
  async uploadUserAvatar(id: string, file: File): Promise<UserResponse> {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.put(`/users/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  /**
   * Get users for dropdown (no pagination, no permissions required)
   * Returns minimal user data for use in filters/selects across modules
   */
  async getUsersForDropdown(filters?: {
    status?: 'active' | 'inactive';
    /**
     * Restringe a users que compartilham team com o user logado.
     * - `'team'`: backend filtra via team-users
     * - `'all'` ou ausente: retorna todos (comportamento legado)
     * Usado pelo CRM pra exibir só operadores elegíveis quando o user
     * tem `userScope === 'team'` no funil ativo (D-context-gate).
     */
    scope?: 'team' | 'all';
  }): Promise<Array<{ id: string; name: string; email: string; avatar?: string }>> {
    const response = await api.get('/users/dropdown', { params: filters });
    const users = response.data.data;

    // Map UserResponse (firstName + lastName) to dropdown format (name)
    return users.map((user: any) => ({
      id: user.id,
      name: `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`.trim(),
      email: user.email,
      avatar: user.avatar
    }));
  },
};
